/**
 * Highscore Manager
 *
 * Manages highscore persistence using localStorage.
 * Tracks max height reached and best completion time.
 */

export interface HighscoreData {
  maxHeight: number;
  bestTime: number | null; // milliseconds, null if never completed
  lastUpdated: number; // timestamp
}

export class HighscoreManager {
  private static readonly STORAGE_KEY = 'blobo_highscores';
  private currentData: HighscoreData;

  constructor() {
    this.currentData = this.loadFromStorage();
  }

  /**
   * Load highscore data from localStorage
   */
  private loadFromStorage(): HighscoreData {
    try {
      const stored = localStorage.getItem(HighscoreManager.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored) as HighscoreData;
        return data;
      }
    } catch (error) {
      console.error('Failed to load highscore data:', error);
    }

    // Return default data if none exists
    return {
      maxHeight: 0,
      bestTime: null,
      lastUpdated: Date.now(),
    };
  }

  /**
   * Save highscore data to localStorage
   */
  private saveToStorage(): void {
    try {
      this.currentData.lastUpdated = Date.now();
      localStorage.setItem(HighscoreManager.STORAGE_KEY, JSON.stringify(this.currentData));
    } catch (error) {
      console.error('Failed to save highscore data:', error);
    }
  }

  /**
   * Get current max height
   */
  public getMaxHeight(): number {
    return this.currentData.maxHeight;
  }

  /**
   * Get best completion time
   */
  public getBestTime(): number | null {
    return this.currentData.bestTime;
  }

  /**
   * Update max height if current height is higher
   * Returns true if a new record was set
   */
  public updateMaxHeight(height: number): boolean {
    if (height > this.currentData.maxHeight) {
      this.currentData.maxHeight = height;
      this.saveToStorage();
      return true;
    }
    return false;
  }

  /**
   * Update best time if current time is faster
   * Returns true if a new record was set
   */
  public updateBestTime(timeMs: number): boolean {
    if (this.currentData.bestTime === null || timeMs < this.currentData.bestTime) {
      this.currentData.bestTime = timeMs;
      this.saveToStorage();
      return true;
    }
    return false;
  }

  /**
   * Reset all highscores
   */
  public reset(): void {
    this.currentData = {
      maxHeight: 0,
      bestTime: null,
      lastUpdated: Date.now(),
    };
    this.saveToStorage();
  }

  /**
   * Get formatted max height string
   */
  public getFormattedMaxHeight(): string {
    return `${Math.round(this.currentData.maxHeight)}m`;
  }

  /**
   * Get formatted best time string
   */
  public getFormattedBestTime(): string {
    if (this.currentData.bestTime === null) {
      return '--:--:---';
    }

    const minutes = Math.floor(this.currentData.bestTime / 60000);
    const seconds = Math.floor((this.currentData.bestTime % 60000) / 1000);
    const ms = this.currentData.bestTime % 1000;

    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }
}
